import React from 'react';
import {
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  ACTION_KIND_OPTIONS,
  LOGIC_KIND_OPTIONS,
  OUTCOME_CATEGORY_OPTIONS,
} from './agentFlowCatalog';

const K = { navy: '#121826' };

export function AgentFlowNodeProperties({ selectedNode, updateSelectedData, kobraFieldSx }) {
  if (!selectedNode) {
    return (
      <Typography variant="body2" color="text.secondary">
        Selecciona un nodo en el lienzo para ver la capa Lógica, Acción u Outcome.
      </Typography>
    );
  }

  if (selectedNode.type === 'start') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          Capa: Trigger (nodo de entrada del grafo)
        </Typography>
        <TextField
          label="Etiqueta"
          fullWidth
          size="small"
          value={selectedNode.data?.label ?? ''}
          onChange={(e) => updateSelectedData({ label: e.target.value })}
          sx={kobraFieldSx}
        />
        <TextField
          label="Nota (opcional)"
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder="Alinea este nodo con el Trigger del panel izquierdo"
          value={selectedNode.data?.triggerHint ?? ''}
          onChange={(e) => updateSelectedData({ triggerHint: e.target.value })}
          sx={kobraFieldSx}
        />
      </Stack>
    );
  }

  if (selectedNode.type === 'message') {
    const kind = selectedNode.data?.actionKind || 'dynamic_message';
    return (
      <Stack spacing={1.5}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: K.navy, mb: 0.5, display: 'block' }}>
          Capa: Acción
        </Typography>
        <FormControl fullWidth size="small" sx={{ ...kobraFieldSx, mb: 1 }}>
          <InputLabel>Tipo de acción</InputLabel>
          <Select
            label="Tipo de acción"
            value={kind}
            onChange={(e) => updateSelectedData({ actionKind: e.target.value })}
          >
            {ACTION_KIND_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {kind === 'whatsapp_template' && (
          <TextField
            label="Nombre / ID plantilla"
            fullWidth
            size="small"
            value={selectedNode.data?.templateName ?? ''}
            onChange={(e) => updateSelectedData({ templateName: e.target.value })}
            sx={{ ...kobraFieldSx, mb: 1 }}
            helperText="WhatsApp Business: nombre o ID según integración"
          />
        )}

        {(kind === 'dynamic_message' || kind === 'assign_human' || kind === 'request_evidence') && (
          <TextField
            label={kind === 'dynamic_message' ? 'Mensaje' : 'Detalle / instrucción'}
            fullWidth
            multiline
            minRows={3}
            value={selectedNode.data?.message ?? ''}
            onChange={(e) => updateSelectedData({ message: e.target.value })}
            sx={{ ...kobraFieldSx, mb: 1 }}
          />
        )}

        {kind === 'note' && (
          <TextField
            label="Texto de la nota"
            fullWidth
            multiline
            minRows={2}
            value={selectedNode.data?.noteBody ?? ''}
            onChange={(e) => updateSelectedData({ noteBody: e.target.value })}
            sx={{ ...kobraFieldSx, mb: 1 }}
          />
        )}

        {kind === 'reminder' && (
          <TextField
            label="Descripción del recordatorio"
            fullWidth
            size="small"
            value={selectedNode.data?.reminderLabel ?? ''}
            onChange={(e) => updateSelectedData({ reminderLabel: e.target.value })}
            sx={{ ...kobraFieldSx, mb: 1 }}
          />
        )}

        {kind === 'block_gestiones' && (
          <TextField
            label="Horas de bloqueo"
            fullWidth
            size="small"
            type="number"
            inputProps={{ min: 0 }}
            value={selectedNode.data?.blockDurationHours ?? ''}
            onChange={(e) => updateSelectedData({ blockDurationHours: e.target.value })}
            sx={kobraFieldSx}
            helperText="Duración aproximada; el motor validará reglas de negocio"
          />
        )}

        {['auto_call', 'mark_sensitive'].includes(kind) && (
          <FormHelperText sx={{ mt: 0 }}>
            Configura parámetros de canal en la integración; aquí solo referencia de flujo.
          </FormHelperText>
        )}
      </Stack>
    );
  }

  if (selectedNode.type === 'condition') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: K.navy, mb: 0.5, display: 'block' }}>
          Capa: Lógica
        </Typography>
        <FormControl fullWidth size="small" sx={{ ...kobraFieldSx, mb: 1 }}>
          <InputLabel>Modo de decisión</InputLabel>
          <Select
            label="Modo de decisión"
            value={selectedNode.data?.logicKind || 'simple'}
            onChange={(e) => updateSelectedData({ logicKind: e.target.value })}
          >
            {LOGIC_KIND_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Condición / regla"
          fullWidth
          multiline
          minRows={2}
          value={selectedNode.data?.expression ?? ''}
          onChange={(e) => updateSelectedData({ expression: e.target.value })}
          sx={kobraFieldSx}
          helperText="Ramas sí/no desde los puntos inferiores. Scoring / IA: describe criterios aquí hasta integrar motor."
        />
      </Stack>
    );
  }

  if (selectedNode.type === 'end') {
    return (
      <Stack spacing={1.5}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: K.navy, mb: 0.5, display: 'block' }}>
          Capa: Outcome
        </Typography>
        <FormControl fullWidth size="small" sx={{ ...kobraFieldSx, mb: 1.5 }}>
          <InputLabel>Resultado esperado</InputLabel>
          <Select
            label="Resultado esperado"
            value={selectedNode.data?.outcomeCategory || 'other'}
            onChange={(e) => updateSelectedData({ outcomeCategory: e.target.value })}
          >
            {OUTCOME_CATEGORY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Etiqueta"
          fullWidth
          size="small"
          value={selectedNode.data?.label ?? ''}
          onChange={(e) => updateSelectedData({ label: e.target.value })}
          sx={{ ...kobraFieldSx, mb: 1.5 }}
        />
        <TextField
          label="Código outcome (opcional)"
          fullWidth
          size="small"
          placeholder="ej. promesa_hecha, sin_respuesta"
          value={selectedNode.data?.outcomeKey ?? ''}
          onChange={(e) => updateSelectedData({ outcomeKey: e.target.value })}
          sx={kobraFieldSx}
          helperText="Identificador estable para analítica y handoff a otros flujos"
        />
      </Stack>
    );
  }

  return null;
}
